import { memo } from "react";

const fmtDate = str =>
  new Date(str + "T12:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

const fmtMoney = n =>
  `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const td = {
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid #f0f0f0",
  fontSize: "0.88rem",
  color: "#333",
  verticalAlign: "middle",
};

const APPROVAL_BADGE = {
  pending:  { bg: "#fff8ee", color: "#7a4a10", border: "#FBC490", label: "Pending" },
  approved: { bg: "#eaf6ef", color: "#1e6645", border: "#6abf85", label: "Approved" },
  rejected: { bg: "#fde8e8", color: "#A12710", border: "#f5a5a5", label: "Rejected" },
};

function ExpenseCard({ expense, onEdit, onDelete, onApprove, onReject, canApprove, projectsLookup, contactsLookup }) {
  const { id, title, amount, date, category, project_id, contact_id, is_billable, is_billed, receipt_url, approval_status, rejection_reason } = expense;
  const status = approval_status || "approved";
  const statusBadge = APPROVAL_BADGE[status] || APPROVAL_BADGE.approved;

  let billableBadge = null;
  if (is_billable && !is_billed) {
    billableBadge = (
      <span style={{
        background: "#fff8ee", color: "#7a4a10",
        border: "1px solid #FBC490",
        borderRadius: 6, padding: "0.1rem 0.45rem",
        fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
      }}>
        Unbilled
      </span>
    );
  } else if (is_billable && is_billed) {
    billableBadge = (
      <span style={{
        background: "#eaf6ef", color: "#1e6645",
        border: "1px solid #6abf85",
        borderRadius: 6, padding: "0.1rem 0.45rem",
        fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
      }}>
        Billed
      </span>
    );
  }

  return (
    <tr
      style={{ background: "#fff", transition: "background 0.12s" }}
      onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
    >
      <td data-label="Date" style={td}>{fmtDate(date)}</td>

      <td data-label="Title" style={{ ...td, fontWeight: 600, color: "#0C2D48", maxWidth: 200 }}>
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </div>
      </td>

      <td data-label="Category" style={td}>
        {category ? (
          <span style={{
            background: "#f0f4f8", color: "#35454C",
            borderRadius: 6, padding: "0.1rem 0.5rem",
            fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
          }}>
            {category}
          </span>
        ) : <span style={{ color: "#ddd" }}>—</span>}
      </td>

      <td data-label="Project" style={{ ...td, color: project_id ? "#2a6080" : "#ccc", fontWeight: project_id ? 600 : 400 }}>
        {project_id ? (projectsLookup[project_id] || "—") : "—"}
      </td>

      <td data-label="Supplier" style={{ ...td, color: "#888" }}>
        {contact_id ? (contactsLookup[contact_id] || "—") : "—"}
      </td>

      <td data-label="Amount" style={{ ...td, textAlign: "right", fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap" }}>
        {fmtMoney(amount)}
      </td>

      <td data-label="Billable" style={{ ...td, textAlign: "center" }}>
        {billableBadge || <span style={{ color: "#ddd" }}>—</span>}
      </td>

      <td data-label="Status" style={{ ...td, textAlign: "center" }}>
        <span
          title={status === "rejected" && rejection_reason ? `Rejected: ${rejection_reason}` : statusBadge.label}
          style={{
            background: statusBadge.bg, color: statusBadge.color,
            border: `1px solid ${statusBadge.border}`, borderRadius: 6,
            padding: "0.1rem 0.45rem", fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
          }}
        >
          {statusBadge.label}
        </span>
      </td>

      <td data-label="Receipt" style={{ ...td, textAlign: "center" }}>
        {receipt_url
          ? <span title="Receipt uploaded" style={{ fontSize: "1rem" }}>📎</span>
          : <span style={{ color: "#ddd" }}>—</span>}
      </td>

      <td data-label="Actions" style={{ ...td, whiteSpace: "nowrap" }}>
        {canApprove && status === "pending" && (
          <>
            <button
              onClick={() => onApprove(id)}
              style={{
                background: "#eaf6ef", border: "1px solid #6abf85", borderRadius: 6,
                padding: "0.25rem 0.65rem", cursor: "pointer", fontSize: "0.78rem",
                fontFamily: "Raleway, sans-serif", color: "#1e6645", marginRight: "0.35rem",
              }}
            >
              Approve
            </button>
            <button
              onClick={() => onReject(id)}
              style={{
                background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 6,
                padding: "0.25rem 0.65rem", cursor: "pointer", fontSize: "0.78rem",
                fontFamily: "Raleway, sans-serif", color: "#A12710", marginRight: "0.35rem",
              }}
            >
              Reject
            </button>
          </>
        )}
        <button
          onClick={() => onEdit(expense)}
          style={{
            background: "#f3f3f3", border: "1px solid #ddd", borderRadius: 6,
            padding: "0.25rem 0.65rem", cursor: "pointer",
            fontSize: "0.78rem", fontFamily: "Raleway, sans-serif",
            marginRight: "0.35rem",
          }}
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(id)}
          style={{
            background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 6,
            padding: "0.25rem 0.65rem", cursor: "pointer",
            fontSize: "0.78rem", fontFamily: "Raleway, sans-serif",
            color: "#A12710",
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export default memo(ExpenseCard);
